import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { tokens } from '../../../../theme.js';
import ConnString from '../../../../ConnString.js';

const PRINT_STYLE_ID = 'ar-aging-print-style';

const injectPrintStyle = () => {
  if (document.getElementById(PRINT_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.innerHTML = `
    @page {
      size: A4 landscape;
      margin: 8mm;
    }

    @media print {
      body * { visibility: hidden; }
      #ar-aging-printable, #ar-aging-printable * { visibility: visible; }
      #ar-aging-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 8px; }
      .ar-aging-no-print { display: none !important; }
      .ar-aging-print-header { display: block !important; visibility: visible; font-size: 14px; font-weight: bold; margin-bottom: 4px; }
      .ar-aging-print-meta { display: block !important; visibility: visible; font-size: 10px; color: #555; margin-bottom: 10px; }
      table {
        border-collapse: collapse;
        width: 100%;
        table-layout: fixed;
        font-size: 9px;
      }
      th, td {
        border: 1px solid #ccc;
        padding: 2px 4px;
        white-space: normal !important;
        word-break: break-word;
        overflow-wrap: anywhere;
        line-height: 1.15;
        vertical-align: top;
      }
      th { background-color: #2563eb !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .ar-aging-detail-print th,
      .ar-aging-detail-print td {
        font-size: 8px;
        padding: 2px 3px;
      }
      .ar-aging-total-row td { font-weight: bold; background-color: #e8eeff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
  document.head.appendChild(style);
};

const Connstring2 = ConnString.connstring;
const hiddenColumns = new Set(['AgentCode', 'AccountCode', 'Account', 'AccountTitle', 'Terms', 'Remarks', 'remarks', 'Refno', 'refno']);
const totalAmountColumn = 'Total Amount';
const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const isAgingBucketColumn = (columnName) => {
  const normalizedColumn = String(columnName).trim();

  return /\bcurrent\b|\bover\s*\d+\s*days?\b|\b\d+\s*-\s*\d+\s*days?\b|\b\d+\s*days?\b/i.test(normalizedColumn);
};

const getTodayValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isNumericLike = (value) => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    return /^-?\d+(\.\d+)?$/.test(value.replace(/,/g, '').trim());
  }

  return false;
};

const getNumericValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.replace(/,/g, '').trim())) {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const formatCellValue = (value, columnName = '') => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Reference-like fields must stay as text (no numeric formatting).
  if (/ref|reference/i.test(String(columnName))) {
    return String(value);
  }

  const numericValue = getNumericValue(value);
  if (numericValue !== null) {
    return numberFormatter.format(numericValue);
  }

  if (typeof value === 'string') {
    const maybeDate = new Date(value);
    if (!Number.isNaN(maybeDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return maybeDate.toLocaleDateString();
    }

    return value;
  }

  return String(value);
};

const downloadFile = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const buildReference = (row) => {
  return [row?.Refno, row?.Description, row?.Particulars]
    .map((value) => String(value ?? '').trim())
    .filter((value) => value !== '')
    .join(' | ');
};

const Report_ARAgingReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dateCutoff, setDateCutoff] = useState(getTodayValue());
  const [reportType, setReportType] = useState('Summary');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasRun, setHasRun] = useState(false);
  const [compID] = useState(() => sessionStorage.getItem('compID'));
  const [compName] = useState(() => sessionStorage.getItem('compName') || sessionStorage.getItem('selectedCompany') || '');

  const rawColumns = useMemo(() => {
    if (!rows.length) {
      return [];
    }

    return Object.keys(rows[0]).filter((column) => !hiddenColumns.has(column));
  }, [rows]);

  const amountColumns = useMemo(() => {
    return rawColumns.filter((column) => {
      if (!rows.length || !rows.every((row) => isNumericLike(row[column]))) {
        return false;
      }

      if (/code|id|no\b|number|qty|count|term/i.test(column)) {
        return false;
      }

      if (/days?|aging/i.test(column) && !isAgingBucketColumn(column)) {
        return false;
      }

      return true;
    });
  }, [rawColumns, rows]);

  const getRowTotal = (row) => {
    return amountColumns.reduce((sum, column) => {
      const value = getNumericValue(row[column]);
      return sum + (value ?? 0);
    }, 0);
  };

  const sourceRows = useMemo(() => {
    return rows
      .filter((row) => getRowTotal(row) !== 0)
      .sort((left, right) => getRowTotal(right) - getRowTotal(left));
  }, [rows, amountColumns]);

  const columns = useMemo(() => {
    if (!sourceRows.length) {
      return [];
    }

    const visibleColumns = Object.keys(sourceRows[0]).filter((column) => !hiddenColumns.has(column));
    const nameIndex = visibleColumns.findIndex((column) => /^(name)$/i.test(column));

    if (nameIndex <= 0) {
      return visibleColumns;
    }

    const [nameColumn] = visibleColumns.splice(nameIndex, 1);
    return [nameColumn, ...visibleColumns];
  }, [sourceRows]);

  const displayColumns = useMemo(() => {
    if (!columns.length) {
      return [];
    }

    return [...columns, totalAmountColumn];
  }, [columns]);

  const numericColumns = useMemo(() => {
    const rowNumericColumns = columns.filter((column) => sourceRows.length > 0 && sourceRows.every((row) => isNumericLike(row[column])));
    return [...rowNumericColumns, totalAmountColumn];
  }, [columns, sourceRows]);

  const totals = useMemo(() => {
    return numericColumns.reduce((accumulator, column) => {
      if (column === totalAmountColumn) {
        accumulator[column] = sourceRows.reduce((sum, row) => sum + getRowTotal(row), 0);
        return accumulator;
      }

      accumulator[column] = sourceRows.reduce((sum, row) => {
        const value = getNumericValue(row[column]);
        return sum + (value ?? 0);
      }, 0);
      return accumulator;
    }, {});
  }, [getRowTotal, numericColumns, sourceRows]);

  const hasTotals = numericColumns.length > 0 && sourceRows.length > 0;

  const grandTotalChips = useMemo(() => {
    if (!hasTotals) {
      return [];
    }

    const priorityPattern = /balance|amount|total|outstanding|current|overdue|aging/i;
    const candidates = numericColumns.filter(
      (col) => col !== totalAmountColumn && priorityPattern.test(col) && totals[col] !== undefined,
    );

    const chips = candidates.map((col) => ({ label: col, value: totals[col] }));
    chips.push({ label: totalAmountColumn, value: totals[totalAmountColumn] });
    return chips;
  }, [hasTotals, numericColumns, totals]);

  const handlePrint = () => {
    injectPrintStyle();
    window.print();
  };

  const getColumnAlignment = (column) => {
    if (numericColumns.includes(column)) {
      return 'right';
    }

    if (/date|aging|cutoff/i.test(column)) {
      return 'center';
    }

    return 'left';
  };

  const getColumnValue = (row, column) => {
    if (column === totalAmountColumn) {
      return getRowTotal(row);
    }

    return row[column];
  };

  const runReport = async () => {
    if (!compID) {
      setError('No company is selected in the current session.');
      setRows([]);
      setHasRun(true);
      return;
    }

    if (!dateCutoff) {
      setError('Cutoff date is required.');
      setRows([]);
      setHasRun(true);
      return;
    }

    setLoading(true);
    setError('');
    setHasRun(true);

    try {
      const response = await fetch(`${Connstring2}/api/Reports/ARAging`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: compID,
          datecutoff: dateCutoff,
          type: reportType,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        setRows([]);
        setError(data?.message || 'Unable to generate A/R Aging report.');
        return;
      }

      const incomingRows = Array.isArray(data?.rows) ? data.rows : [];
      const normalizedRows = reportType === 'Detail'
        ? incomingRows.map((row) => {
          const { Refno, Description, Particulars, ...rest } = row || {};
          return {
            Refno,
            Reference: buildReference({ Refno, Description, Particulars }),
            ...rest,
          };
        })
        : incomingRows;

      setRows(normalizedRows);
    } catch (requestError) {
      console.error('Error generating A/R Aging report:', requestError);
      setRows([]);
      setError('Unable to generate A/R Aging report due to network/server error.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!sourceRows.length || !displayColumns.length) {
      return;
    }

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const lines = [displayColumns.map(escapeCsv).join(',')];

    sourceRows.forEach((row) => {
      lines.push(displayColumns.map((column) => escapeCsv(getColumnValue(row, column))).join(','));
    });

    if (hasTotals) {
      lines.push(displayColumns.map((column, index) => {
        if (index === 0) {
          return escapeCsv('TOTAL');
        }

        return numericColumns.includes(column) ? escapeCsv(totals[column]) : escapeCsv('');
      }).join(','));
    }

    downloadFile(lines.join('\n'), `AR_Aging_${reportType}_${dateCutoff}.csv`, 'text/csv;charset=utf-8;');
  };

  const handleExportExcel = () => {
    if (!sourceRows.length || !displayColumns.length) {
      return;
    }

    const headerHtml = displayColumns.map((column) => `<th>${column}</th>`).join('');
    const bodyHtml = sourceRows.map((row) => `<tr>${displayColumns.map((column) => `<td>${String(getColumnValue(row, column) ?? '')}</td>`).join('')}</tr>`).join('');
    const totalHtml = hasTotals
      ? `<tr>${displayColumns.map((column, index) => {
        if (index === 0) {
          return '<td><strong>TOTAL</strong></td>';
        }

        return numericColumns.includes(column) ? `<td><strong>${totals[column]}</strong></td>` : '<td></td>';
      }).join('')}</tr>`
      : '';
    const html = `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}${totalHtml}</tbody></table>`;

    downloadFile(`\ufeff${html}`, `AR_Aging_${reportType}_${dateCutoff}.xls`, 'application/vnd.ms-excel;charset=utf-8;');
  };

  const summaryCards = [
    { label: 'Rows Returned', value: sourceRows.length, accent: colors.blueAccent?.[500] ?? '#6870fa' },
    { label: 'Cutoff Date', value: dateCutoff || '—', accent: colors.greenAccent?.[500] ?? '#4cceac' },
    { label: 'Report Type', value: reportType, accent: colors.redAccent?.[400] ?? '#e2726e' },
    { label: 'Numeric Columns', value: amountColumns.length, accent: colors.blueAccent?.[300] ?? '#9bb4ff' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(14,116,144,0.95), rgba(59,130,246,0.9))'
            : 'linear-gradient(135deg, #2563eb, #38bdf8)',
          color: '#fff',
          boxShadow: '0 20px 45px rgba(37, 99, 235, 0.24)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              A/R Aging Report
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Run the stored procedure with a cutoff date and either Summary or Detail output.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              onClick={runReport}
              disabled={loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              {loading ? 'Running...' : 'Run Report'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportExcel}
              disabled={!sourceRows.length || loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              Export Excel
            </Button>
            <Button
              variant="contained"
              onClick={handleExportCsv}
              disabled={!sourceRows.length || loading}
              sx={{ bgcolor: '#fff', color: '#2563eb', '&:hover': { bgcolor: '#f8fafc' } }}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintOutlinedIcon />}
              onClick={handlePrint}
              disabled={!sourceRows.length || loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              Print
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Paper key={card.label} elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}` }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{card.label}</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: card.accent }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}` }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>Parameters</Typography>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
            <TextField
              size="small"
              label="Date Cutoff"
              type="date"
              value={dateCutoff}
              onChange={(event) => setDateCutoff(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            />
            <TextField
              select
              size="small"
              label="Type"
              value={reportType}
              onChange={(event) => setReportType(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 160 } }}
            >
              <MenuItem value="Summary">Summary</MenuItem>
              <MenuItem value="Detail">Detail</MenuItem>
            </TextField>
            <Button variant="contained" startIcon={<AssessmentOutlinedIcon />} onClick={runReport} disabled={loading}>
              {loading ? 'Running...' : 'Generate'}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="Branch Code: 0000" color="primary" variant="outlined" />
            <Chip label="Customer: ALL" color="primary" variant="outlined" />
            <Chip label="GL Account: 4200" color="primary" variant="outlined" />
          </Stack>
          {error && (
            <Typography variant="body2" color="error">{error}</Typography>
          )}
        </Stack>
      </Paper>

      {grandTotalChips.length > 0 && (
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}` }} className="ar-aging-no-print">
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Grand Totals
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {grandTotalChips.map((chip) => (
              <Chip
                key={chip.label}
                label={`${chip.label}: ${numberFormatter.format(chip.value)}`}
                color="primary"
                sx={{ fontWeight: 700, fontSize: '0.82rem' }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      <Paper
        id="ar-aging-printable"
        className={reportType === 'Detail' ? 'ar-aging-detail-print' : ''}
        elevation={0}
        sx={{ borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, overflow: 'hidden' }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}` }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Box className="ar-aging-print-header" sx={{ display: 'none', visibility: 'hidden' }}>{compName ? `${compName} — ` : ''}A/R Aging Report</Box>
              <Box className="ar-aging-print-meta" sx={{ display: 'none', visibility: 'hidden' }}>Cutoff: {dateCutoff} &nbsp;|&nbsp; Type: {reportType} &nbsp;|&nbsp; Branch: 0000 &nbsp;|&nbsp; Customer: ALL &nbsp;|&nbsp; GL: 4200</Box>
              <Typography variant="h6" fontWeight={700} className="ar-aging-no-print">Report Output</Typography>
              <Typography variant="body2" color="text.secondary" className="ar-aging-no-print">
                Stored procedure output for the selected cutoff date and report type.
              </Typography>
            </Box>
            {hasTotals && (
              <Chip label={`${sourceRows.length} rows · ${amountColumns.length} amount column${amountColumns.length === 1 ? '' : 's'}`} color="primary" variant="outlined" className="ar-aging-no-print" />
            )}
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !hasRun ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>Run the report to view results</Typography>
            <Typography variant="body2" color="text.secondary">Select the cutoff date and report type, then click Generate.</Typography>
          </Box>
        ) : sourceRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>No rows returned</Typography>
            <Typography variant="body2" color="text.secondary">The stored procedure returned no records with a non-zero total amount for the selected parameters.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {displayColumns.map((column) => (
                    <TableCell key={column} align={getColumnAlignment(column)} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sourceRows.map((row, rowIndex) => (
                  <TableRow key={`ar-aging-row-${rowIndex}`} hover>
                    {displayColumns.map((column) => (
                      <TableCell key={`${column}-${rowIndex}`} align={getColumnAlignment(column)} sx={{ whiteSpace: 'nowrap' }}>
                        {formatCellValue(getColumnValue(row, column), column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {hasTotals && (
                  <TableRow className="ar-aging-total-row" sx={{ '& td': { fontWeight: 700, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(37,99,235,0.06)' } }}>
                    {displayColumns.map((column, index) => (
                      <TableCell key={`total-${column}`} align={getColumnAlignment(column)} sx={{ whiteSpace: 'nowrap' }}>
                        {index === 0 ? 'TOTAL' : numericColumns.includes(column) ? formatCellValue(totals[column], column) : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default Report_ARAgingReport;
